import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaBox, FaXmark } from 'react-icons/fa6';
import { useCart, useRemoveFromCart } from '../../../api/queryHooks/order';
import { mediaThumbUrl } from '../../../utils/picUrl';
import MediaThumbnail from '../../common/MediaThumbnail';
import { useCurrency } from '../../../hooks/useCurrency';
import { Loader } from '@f2m3/shared-frontend-ui';
import PromoPrice from '../../common/PromoPrice';
import type { Uuid } from '../../../api/types';

interface CartIndicatorBodyProps {
  gotoOrderClick: () => void;
}

const CartIndicatorBody = ({ gotoOrderClick }: CartIndicatorBodyProps) => {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { data, isLoading } = useCart();
  const removeFromCart = useRemoveFromCart();

  const totalPrice =
    data?.line_items?.reduce(
      (sum, item) => sum + parseFloat(String((item.promotion ? item.effective_price : item.price) ?? 0)),
      0,
    ) || 0;
  const itemCount = data?.line_items?.length || 0;

  const handleDeleteClick = (lineItemId: Uuid, e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    removeFromCart.mutate({ line_item_id: lineItemId });
  };

  return (
    <>
      <div className="cart-indicator-body">
        <div className="cart-header">
          <h3 className="cart-title">{t('cart.title')}</h3>
          <span className="cart-count">{itemCount} {itemCount === 1 ? t('cart.item') : t('cart.items')}</span>
        </div>

        { isLoading && (
          <div className="cart-loading">
            <Loader />
          </div>
        )}

        { !isLoading && data?.line_items?.length === 0 && (
          <div className="cart-empty">
            <p>{t('cart.empty')}</p>
          </div>
        )}

        { !isLoading && data?.line_items && data.line_items.length > 0 && (
          <>
            <div className="cart-items">
              { data.line_items.map((item) => (
                <div key={item.id} className="cart-item-compact">
                  {/* Tiny thumbnail */}
                  <div className="cart-item-thumb">
                    {item.pics && item.pics.length > 0 ? (
                      <MediaThumbnail src={mediaThumbUrl(item.pics[0])} alt="" className="cart-thumb-image" />
                    ) : (
                      <div className="cart-thumb-placeholder">
                        <FaBox size={12} />
                      </div>
                    )}
                  </div>

                  {/* Product info */}
                  <div className="cart-item-info">
                    <div className="cart-item-name">{item.product?.name}</div>
                    <div className="cart-item-qty">
                      {t('cart.qty_short')}: {item.quantity}
                    </div>
                  </div>

                  {/* Line price */}
                  <div className="cart-item-price">
                    <PromoPrice original={item.price} effective={item.effective_price} promotion={item.promotion} />
                  </div>

                  {/* Delete button */}
                  <button
                    className="cart-item-delete"
                    onClick={(e) => handleDeleteClick(item.id, e)}
                    disabled={removeFromCart.isPending}
                    title={t('cart.delete')}
                  >
                    <FaXmark />
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-footer">
              <div className="cart-total">
                <span className="cart-total-label">{t('cart.total')}:</span>
                <span className="cart-total-amount">{format(totalPrice)}</span>
              </div>
              <button className="button is-primary is-fullwidth cart-checkout-btn" onClick={gotoOrderClick}>
                <span>{t('cart.checkout')}</span>
                <FaArrowRight className="ml-2" />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CartIndicatorBody;
